import { Component } from 'react';

import AppState from '../../AppState';
import { loadScriptAsync } from '../common';
import HeaderLevel4 from '../components/Typography/HeaderLevel4';
import Logger from '../Logger';
import { HubSpotFormState } from './HubSpotFormState';
import hubSpotCSS from './HubSpotFormV2.css';
import HubSpotLocaleResolver from './HubSpotLocaleResolver';

export interface HubSpotFormV2Props<T> {
  appState: AppState;
  state: T;
  onFormSubmit: (data: Record<string, string | string[]>) => void;
  initialFormData?: Record<string, string>;
}

type HubSpotMessage = { type: string; eventName: string };

export class HubSpotFormV2<T extends HubSpotFormState> extends Component<HubSpotFormV2Props<T>> {
  public hubSpotFormScriptUrl = '//js-eu1.hsforms.net/forms/embed/v2.js';
  public requestId = `hubSpotFormContainer`;

  get formElement() {
    const formContainer = document.querySelector(`#${this.requestId}`);
    return formContainer?.querySelector('form');
  }

  get formData(): Record<string, string | string[]> {
    const form = this.formElement;

    if (!form) {
      return {};
    }

    const result: Record<string, string | string[]> = {};

    new FormData(form).forEach((value, name) => {
      const text = value.toString();
      const existing = result[name];

      if (existing === undefined) {
        result[name] = text;
      } else if (Array.isArray(existing)) {
        existing.push(text);
      } else {
        result[name] = [existing, text];
      }
    });

    return result;
  }

  componentDidMount() {
    this.messageHandler = this.messageHandler.bind(this);
    this.props.appState.loadingPageIndicator.start();
    this.attachErrorHandler();
    this.embedHubSpotForm();
  }

  componentWillUnmount() {
    this.detachErrorHandler();
  }

  onFormError(error: Error): void {
    this.props.state.setError(error);
    this.props.appState.loadingPageIndicator.stop();
  }

  onFormSubmitted() {
    this.props.state.setFormSubmitted();
  }

  attachErrorHandler() {
    window.addEventListener('message', this.messageHandler);
  }

  detachErrorHandler() {
    window.removeEventListener('message', this.messageHandler);
  }

  // Using global form events, see more https://developers.hubspot.com/global-form-events
  messageHandler(err: MessageEvent) {
    if (err.data) {
      const dataObject = err.data as HubSpotMessage;
      if (dataObject.eventName === 'onFormDefinitionFetchError' && dataObject.type === 'hsFormCallback') {
        this.onFormError(new Error('Failed to fetch HubSpot form definition'));
      }
    }
  }

  embedHubSpotForm() {
    const { state } = this.props;
    const { portalId, formId } = state.hubSpotConfig;
    const languageCode = this.props.state.shoppingContext.languageCode;
    loadScriptAsync(this.hubSpotFormScriptUrl).then(() => {
      window.hbspt.forms.create({
        portalId: portalId,
        formId: formId,
        css: hubSpotCSS,
        locale: HubSpotLocaleResolver.resolve(languageCode),
        env: '',
        target: `#${this.requestId}`,
        onFormReady: () => {
          this.setFormData(this.props.initialFormData);
          this.props.appState.loadingPageIndicator.stop();
        },
        onFormSubmit: () => {
          this.props.onFormSubmit(this.formData);
        },
        onFormSubmitted: () => {
          this.onFormSubmitted();
        },
      });
    });
  }

  setFormData(data: Record<string, string>) {
    const formElement = this.formElement;

    if (!formElement) {
      return;
    }

    const setInputValue = (name: string, value: string) => {
      const field: HTMLInputElement = formElement.querySelector(`input[name="${name}"]`);

      if (!field) {
        Logger.warn(`Field ${name} not found in form ${formElement.id}`);
        return;
      }

      field.value = value;
    };

    Object.entries(data).forEach(([name, value]) => setInputValue(name, value));
  }

  render() {
    if (this.props.state.error != null) {
      return (
        <div>
          <HeaderLevel4>{this.props.state.translations.errorMessage}</HeaderLevel4>
        </div>
      );
    } else {
      return <div id={this.requestId} />;
    }
  }
}
